import { ARTWORK_SERVICE } from '../api';
/**
 * Sets a cookie with the specified name and value, and optional expiration in days.
 *
 * @param name - The name of the cookie.
 * @param value - The value to store in the cookie.
 * @param days - Optional. The number of days until the cookie expires. Defaults to 0.5.
 */
export const setCookie = (name: string, value: string, days = 0.5) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
};

/**
 * Saves specific URL parameters (accessToken, userId, tenantId, username, email) to cookies.
 */
export const saveUrlAuthParamsToCookie = () => {
  const toUpperSnakeCase = (str: string) => str.replaceAll(/([A-Z])/g, '_$1').toUpperCase();
  const params = new URLSearchParams(globalThis.location.search);
  if (params?.size === 0) return;
  const keys = ['accessToken', 'tenantId', 'userName', 'email', 'userId'];
  let hasParam = false;
  keys.forEach(key => {
    const value = params.get(key);
    if (value) {
      const cookieKey = toUpperSnakeCase(key);
      setCookie(cookieKey, value);
      hasParam = true;
    }
  });

  if (hasParam) {
    const url = globalThis.location.origin + globalThis.location.pathname;
    globalThis.history.replaceState({}, document.title, url);
  }
};


// fetch frontend env,get artwork service url;
export const initEnv = async () => {
  try {
    const frontendEnv = await fetch('/env.json').then(res => res.json());
    let serviceUrl = frontendEnv.serviceUrl;
    if (serviceUrl) {
      let backendEnv = await ARTWORK_SERVICE.envJson.getEnvJson({ baseURL: serviceUrl });
      if (backendEnv?.environment?.moduleUrls && !backendEnv?.environment?.moduleUrls?.artwork) {
        backendEnv.environment.moduleUrls.artwork = serviceUrl;
      }
      (globalThis as any).ARTWORK_ENV = backendEnv;
    } else {
      console.warn('Service URL not found in frontend env');
    }
  } catch (error) {
    console.error('Failed to load environment configuration:', error);
  }

  if (!globalThis.ARTWORK_ENV) {
    throw new Error('Artwork environment configuration not found');
  }
}

/**
 * Triggers a file download by URL using an anchor element (avoids popup blockers).
 *
 * @param url - The download URL (e.g. presigned URL).
 * @param filename - Optional filename for the saved file.
 */
export const downloadByUrl = (url: string, filename?: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener noreferrer';
  if (filename) link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/**
 * Triggers a file download in the browser using a blob.
 *
 * @param blob - The blob data to download.
 * @param filename - The name of the file to be downloaded.
 */
export const downloadBlob = (blob: Blob, filename: string) => {
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link?.remove();
  globalThis.URL.revokeObjectURL(url);
};

/**
 * Downloads a CSV file from string data or Blob.
 *
 * @param data - The CSV data (string or Blob).
 * @param filename - Optional filename. Defaults to `export_{timestamp}.csv`.
 */
export const downloadCSV = (data: string | Blob, filename?: string) => {
  const blob = data instanceof Blob ? data : new Blob([data], { type: 'text/csv;charset=utf-8;' });
  const finalFilename = filename ?? `export_${Date.now()}.csv`;
  downloadBlob(blob, finalFilename);
};

/**
 * Downloads an Excel file (xlsx) from Blob data.
 *
 * @param data - The Excel file data (Blob).
 * @param filename - Optional filename. Defaults to `export_{timestamp}.xlsx`.
 */
export const downloadExcel = (data: Blob, filename?: string) => {

  const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const finalFilename = filename ?? `export_${Date.now()}.xlsx`;
  downloadBlob(blob, finalFilename);
};
